import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Card,
	CardHeader,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import { newsfeedApi } from "../api/newsFeedApi";
import PostItem from "../components/newsfeed/PostItem";
import ComposePost from "../components/newsfeed/ComposePost";
import useUserStore from "../zustand/UseUserStore";
import { useNavigate, useParams } from "react-router-dom";
import useNewsfeedStore from "../zustand/UseNewsfeed";
import { userAuthAPI } from "../api/userAuthAPI";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	ArrowUp,
	Heart,
	Award,
	Star,
	Plus,
	X,
	MessageCircle,
	Send,
	Filter,
	ChevronDown,
	ChevronUp,
} from "lucide-react";
import { useInviteAcceptance } from "../hooks/useInviteAcceptance";
import InviteConfirmationModal from "../components/InviteConfirmationModal";
import { toast, Toaster } from "sonner";

const Newsfeed = ({ showPost = false }) => {
	const { id, postType: urlPostType } = useParams();
	const navigate = useNavigate();

	const newsfeed = useNewsfeedStore();
	const userStore = useUserStore();
	const observer = useRef();

	console.log("Newsfeed component rendering", {
		showPost,
		newsfeed: newsfeed ? "exists" : "undefined",
		userStore: userStore ? "exists" : "undefined",
		posts: newsfeed?.posts,
		user: userStore?.user,
	});

	// Use the invite acceptance hook
	const {
		inviteData,
		isProcessing,
		showModal,
		error,
		isInviteURL,
		setShowModal,
	} = useInviteAcceptance();

	// Only show modal if we're actually in an invite flow
	const shouldShowInviteModal = showModal && inviteData && isInviteURL;

	const contests = [
		"Glow Game",
		...(userStore.getContestNames() || []).filter(
			(name) => name.toLowerCase() !== "the glow game"
		),
	];

	// Debug logging
	console.log("📋 Contests array:", contests);
	console.log(
		"📋 User roles:",
		userStore.user_roles.map((r) => ({
			ContestName: r.ContestName,
			ContestID: r.ContestID,
			Role: r.Role,
		}))
	);

	const sortOptions = ["Newest", "Oldest", "Most Likes", "Most Lumens"];
	const defaultFilters = {
		sort: 0,
		contestid: 0,
	};

	// Post types from mockup
	const postTypes = [
		{
			id: "glow",
			label: "Glow",
			icon: Star,
			color: "bg-yellow-500",
			lumens: "+ 1-10",
			description: "Nice act you did for others",
		},
		{
			id: "pride",
			label: "Proud",
			icon: Award,
			color: "bg-purple-500",
			lumens: "1-5",
			description: "Something you accomplished",
		},
		{
			id: "grateful",
			label: "Grateful",
			icon: Heart,
			color: "bg-green-500",
			lumens: "- 2",
			description: "Someone did something nice for you",
		},
	];

	// Helper function to get contest ID from PrevContest
	const getInitialContestID = () => {
		const prevContestID = userStore.user?.PrevContest;
		if (!prevContestID || prevContestID === 0) {
			return 0; // Default to "Glow Game"
		}

		// Verify the contest exists in user roles
		const prevContestRole = userStore.user_roles.find(
			(role) => role.ContestID === prevContestID
		);

		return prevContestRole ? prevContestID : 0;
	};

	// Helper function to convert contest ID to index for Select component
	const getContestIndexFromID = (contestID) => {
		if (contestID === 0) {
			return 0;
		} // "Glow Game"

		// Find the contest name from user roles
		const contestRole = userStore.user_roles.find(
			(role) => role.ContestID === contestID
		);

		if (contestRole) {
			// Find the index of this contest in the contests array
			const index = contests.findIndex(
				(c) => c.toLowerCase() === contestRole.ContestName.toLowerCase()
			);
			return index > 0 ? index : 0;
		}

		return 0;
	};

	// Helper function to convert contest index to ID for API calls
	const getContestIDFromIndex = (index) => {
		if (index === 0) {
			return 0;
		} // "Glow Game"

		const contestName = contests[index];
		console.log(
			"🔍 getContestIDFromIndex - index:",
			index,
			"contestName:",
			contestName
		);

		// Find all matching roles
		const matchingRoles = userStore.user_roles.filter(
			(role) => role.ContestName.toLowerCase() === contestName.toLowerCase()
		);

		console.log("🔍 Found matching roles:", matchingRoles);

		// Use the first match (there should typically only be one per contest)
		const contestRole = matchingRoles[0];

		const contestID = contestRole ? contestRole.ContestID : 0;
		console.log("🔍 Returning contestID:", contestID);

		return contestID;
	};

	// Local state
	const [selectedPost, setSelectedPost] = useState(null);
	const [showBackToTop, setShowBackToTop] = useState(false);
	const [loading, setLoading] = useState(true);
	const topOfFeedRef = useRef(null);
	const [filters, setFilters] = useState({
		...defaultFilters,
		contestid: getInitialContestID(),
	});
	const [selectedPostType, setSelectedPostType] = useState("glow");
	const [showComments, setShowComments] = useState(true);
	const [showCreatePost, setShowCreatePost] = useState(false);
	const [showPostTypeSelector, setShowPostTypeSelector] = useState(false);
	const [newPostContent, setNewPostContent] = useState("");
	const [selectedCommentsPost, setSelectedCommentsPost] = useState(null);
	const [showSendLumens, setShowSendLumens] = useState(false);
	const [selectedPostForLumens, setSelectedPostForLumens] = useState(null);
	const [lumensToSend, setLumensToSend] = useState(5);
	const [showFilters, setShowFilters] = useState(false);
	// Post type filters: none selected by default
	const [postTypeFilters, setPostTypeFilters] = useState({
		glow: false,
		pride: false,
		grateful: false,
	});

	// Handle URL parameters for contest selection and modal opening
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const contestNameParam = params.get("contestName");
		const openComposeParam = params.get("openCompose");

		if (contestNameParam && openComposeParam === "true") {
			// Find the contest index by name in the contests array
			const contestIndex = contests.findIndex(
				(contest) => contest.toLowerCase() === contestNameParam.toLowerCase()
			);

			if (contestIndex !== -1) {
				// Convert index to contest ID
				const contestID = getContestIDFromIndex(contestIndex);
				// Set the contest filter to match the contest ID
				const newFilters = { ...filters, contestid: contestID };
				setFilters(newFilters);

				// Reload the feed with the new contest filter
				setLoading(true);
				newsfeed.getPosts(1, newFilters).then(() => {
					setLoading(false);
					// Open the compose modal after feed loads
					setShowCreatePost(true);
				});
			}
			// Clear URL params to avoid reopening on refresh
			window.history.replaceState({}, "", window.location.pathname);
		}
	}, [contests]);

	// Loads initial data and initializes infinite scroll observer when component mounts
	useEffect(() => {
		// If on single post page
		if (showPost) {
			// If post was clicked, load related comments
			if (selectedPost) {
				const postItemId = getPostId(selectedPost);
				newsfeed.loadComments(postItemId);
				setLoading(false);
			} else {
				// If directly linked to post, get post and comments
				setLoading(true);
				handleGetPost(id, urlPostType);
			}
		} else {
			// If on newsfeed page, load first page of posts and set loading to false
			newsfeed.getPosts(1, filters).then(() => {
				setLoading(false);
			});
		}

		// Show back to top button if user scrolls down past 800px
		const handleScroll = (e) => {
			const scrollTop = e?.target?.scrollTop || window.scrollY;
			if (scrollTop > 800) {
				setShowBackToTop(true);
			} else {
				setShowBackToTop(false);
			}
		};

		// Find the scroll container (parent with overflow-auto)
		const scrollContainer = document.querySelector(".overflow-auto");

		if (scrollContainer) {
			scrollContainer.addEventListener("scroll", handleScroll);
		} else {
			window.addEventListener("scroll", handleScroll);
		}

		return () => {
			if (scrollContainer) {
				scrollContainer.removeEventListener("scroll", handleScroll);
			} else {
				window.removeEventListener("scroll", handleScroll);
			}
		};
	}, [filters]);

	// Reload posts when filters change
	useEffect(() => {
		// Skip initial load (already handled by the main useEffect above)
		if (loading) {
			return;
		}

		console.log("Filters changed, reloading posts with:", filters);
		setLoading(true);
		newsfeed.getPosts(1, filters).then(() => {
			setLoading(false);
		});
	}, [filters.sort, filters.contestid]); // Only react to sort and contestid changes

	// Infinite scroll observer for loading more posts
	const lastItemRef = (node) => {
		if (loading) {
			return;
		}
		if (observer.current) {
			observer.current.disconnect();
		}

		observer.current = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && newsfeed.hasMorePosts) {
				newsfeed.getPosts(newsfeed.postPageNum + 1, filters);
			}
		});

		if (node) {
			observer.current.observe(node);
		}
	};

	// Function for single post display page
	// Helper function to get the correct ID based on post type
	const getPostId = (post) => {
		if (!post) {
			return null;
		}
		if (post.GratitudeID !== undefined && post.GratitudeID !== null) {
			return post.GratitudeID;
		} else if (post.PrideID !== undefined && post.PrideID !== null) {
			return post.PrideID;
		} else {
			return post.GlowID;
		}
	};

	// Helper function to detect post type
	const detectPostType = (post) => {
		if (post.GratitudeID !== undefined && post.GratitudeID !== null) {
			return "grateful";
		} else if (post.PrideID !== undefined && post.PrideID !== null) {
			return "pride";
		} else if (post.Type === "grateful" || post.Type === "Grateful") {
			return "grateful";
		} else if (post.Type === "pride" || post.Type === "Pride") {
			return "pride";
		} else {
			return "glow";
		}
	};

	// Check if any post type filter is active
	const hasActiveFilters = Object.values(postTypeFilters).some(
		(value) => value === true
	);

	// Filter posts based on selected post types
	const getFilteredPosts = () => {
		if (!hasActiveFilters) {
			// No filters active, show all posts
			return newsfeed.posts || [];
		}
		return (newsfeed.posts || []).filter((post) => {
			const postType = detectPostType(post);
			return postTypeFilters[postType] === true;
		});
	};

	// Toggle post type filter
	const togglePostTypeFilter = (type) => {
		setPostTypeFilters((prev) => ({
			...prev,
			[type]: !prev[type],
		}));
	};

	// Clear all post type filters
	const clearPostTypeFilters = () => {
		setPostTypeFilters({
			glow: false,
			pride: false,
			grateful: false,
		});
	};

	const filteredPosts = getFilteredPosts();

	// Gets one post and related comments by post id
	const handleGetPost = async (id, postType = null) => {
		try {
			console.log("🔍 handleGetPost called:", { id, postType, urlPostType });
			// Use provided postType or urlPostType from route params
			const typeToUse = postType || urlPostType;
			const response = await newsfeedApi.getPost(id, typeToUse);
			console.log("📥 getPost response:", response);
			console.log("📥 response.data:", response.data);
			console.log("📥 response.data.result:", response.data?.result);
			console.log("📥 response.result:", response.result);
			// Fix: API returns response.data.result.post
			const fetchedPost =
				response.data?.result?.post ||
				response.result?.post ||
				response.data?.post;
			console.log("📋 Fetched post:", fetchedPost);

			if (!fetchedPost) {
				console.error("❌ No post found in response");
				setLoading(false);
				return;
			}

			console.log("🔄 Setting selectedPost to:", fetchedPost);
			setSelectedPost(fetchedPost);
			console.log("📌 selectedPost state should update on next render");
			// Use the correct ID from the fetched post
			const postItemId = getPostId(fetchedPost) || id;
			console.log("💬 Loading comments for itemId:", postItemId);
			await newsfeed.loadComments(postItemId);
			console.log("✅ Setting loading to false");
			setLoading(false);
		} catch (error) {
			console.error("❌ Error in handleGetPost:", error);
			setLoading(false);
		}
	};

	// Handle selecting a post to display
	const onPostSelected = (item) => {
		setSelectedPost(item);
		// Use the correct ID based on post type
		const postItemId = getPostId(item);
		newsfeed.loadComments(postItemId);
	};

	const handleSortChange = (key, value) => {
		console.log("sort", key, value);
	};

	if (loading) {
		return (
			<div className="mx-auto w-full max-w-xl p-4">
				<div className="text-foreground dark:text-foreground">Loading...</div>
			</div>
		);
	}

	// Add error boundary check
	try {
		// Test if we can access necessary data
		if (!newsfeed) {
			console.error("Newsfeed store is undefined");
			return (
				<div className="mx-auto w-full max-w-xl p-4">
					<div className="text-red-500">
						Error: Newsfeed store not initialized
					</div>
				</div>
			);
		}
		if (!userStore) {
			console.error("User store is undefined");
			return (
				<div className="mx-auto w-full max-w-xl p-4">
					<div className="text-red-500">Error: User store not initialized</div>
				</div>
			);
		}
	} catch (error) {
		console.error("Error in Newsfeed component initialization:", error);
		return (
			<div className="mx-auto w-full max-w-xl p-4">
				<div className="text-red-500">
					Error loading newsfeed: {error.message}
				</div>
			</div>
		);
	}

	console.log("🎨 Render decision:", {
		showPost,
		selectedPost: !!selectedPost,
		loading,
	});

	if (!showPost) {
		return (
			<div className="min-h-screen w-full overflow-x-hidden bg-gray-50 dark:bg-gray-900">
				<Toaster position="top-center" richColors />
				<div
					ref={topOfFeedRef}
					className="mx-auto flex max-w-3xl items-start gap-2 p-2 lg:gap-6 lg:p-4"
				>
					{/* Desktop Sidebar */}
					<div className="hidden w-64 space-y-4 self-start lg:block">
						<div className="rounded-lg border-2 border-purple-300 bg-white p-4 shadow-lg dark:border-purple-700 dark:bg-gray-800">
							<h3 className="mb-3 font-bold text-gray-900 dark:text-white">
								Create Post Type
							</h3>
							<div className="space-y-2">
								{postTypes.map((type) => {
									const Icon = type.icon;
									const isSelected = selectedPostType === type.id;
									// Get border color based on type
									const getBorderColor = () => {
										if (type.id === "grateful") {
											return "border-green-500";
										}
										if (type.id === "pride") {
											return "border-purple-500";
										}
										if (type.id === "glow") {
											return "border-yellow-500";
										}
										return "border-gray-300";
									};
									return (
										<button
											key={type.id}
											onClick={() => {
												// Toggle: if already selected, default to "glow"; otherwise select
												setSelectedPostType(isSelected ? "glow" : type.id);
											}}
											className={`flex w-full items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
												isSelected
													? `${type.color} text-white ${getBorderColor()}`
													: `bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 ${getBorderColor()}`
											}`}
										>
											<Icon size={20} />
											<div className="text-left">
												<div className="font-medium">{type.label}</div>
												<div className="text-xs opacity-80">
													{type.lumens} lumens
												</div>
												<div className="text-xs opacity-80">
													{type.description}
												</div>
											</div>
										</button>
									);
								})}
							</div>
						</div>

						{/* Post Type Filters */}
						<div className="rounded-lg border-2 border-purple-300 bg-white p-4 shadow-lg dark:border-purple-700 dark:bg-gray-800">
							<div className="mb-3 flex items-center justify-between">
								<h3 className="font-bold text-gray-900 dark:text-white">
									Filter Posts
								</h3>
								{hasActiveFilters && (
									<button
										onClick={clearPostTypeFilters}
										className="text-xs text-purple-600 underline hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
									>
										Show All
									</button>
								)}
							</div>
							<div className="space-y-2">
								{postTypes.map((type) => {
									const Icon = type.icon;
									const isFiltered = postTypeFilters[type.id];
									// Get border color based on type
									const getBorderColor = () => {
										if (type.id === "grateful") {
											return "border-green-500";
										}
										if (type.id === "pride") {
											return "border-purple-500";
										}
										if (type.id === "glow") {
											return "border-yellow-500";
										}
										return "border-gray-300";
									};
									return (
										<button
											key={type.id}
											onClick={() => togglePostTypeFilter(type.id)}
											className={`flex w-full items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
												isFiltered
													? `${type.color} text-white ${getBorderColor()}`
													: `bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 ${getBorderColor()}`
											}`}
										>
											<Icon size={20} />
											<div className="flex-1 text-left">
												<div className="font-medium">{type.label}</div>
											</div>
											{isFiltered && (
												<X
													size={16}
													className="text-white opacity-80 hover:opacity-100"
													onClick={(e) => {
														e.stopPropagation();
														togglePostTypeFilter(type.id);
													}}
												/>
											)}
										</button>
									);
								})}
							</div>
						</div>

						<div className="overflow-hidden rounded-lg border-2 border-purple-300 bg-white shadow-lg dark:border-purple-700 dark:bg-gray-800">
							{/* Filter Toggle Button */}
							<button
								onClick={() => setShowFilters(!showFilters)}
								className="flex w-full items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
							>
								<h3 className="font-bold text-gray-900 dark:text-white">
									Filters
								</h3>
								{showFilters ? (
									<ChevronUp className="h-5 w-5 text-gray-500" />
								) : (
									<ChevronDown className="h-5 w-5 text-gray-500" />
								)}
							</button>

							{/* Collapsible Filter Content */}
							<div
								className={`border-t border-gray-200 transition-all duration-300 ease-in-out dark:border-gray-700 ${
									showFilters
										? "max-h-[600px] opacity-100"
										: "max-h-0 border-t-0 opacity-0"
								}`}
								style={{ overflow: "hidden" }}
							>
								<div className="space-y-4 p-4 pt-4">
									<label className="flex cursor-pointer items-center gap-2">
										<input
											type="checkbox"
											checked={showComments}
											onChange={(e) => setShowComments(e.target.checked)}
											className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-purple-600 accent-purple-600 focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-purple-600"
										/>
										<span className="text-sm text-gray-700 dark:text-gray-300">
											Show Comments
										</span>
									</label>

									{/* Sort Filter */}
									<div className="space-y-1">
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
											Sort By
										</label>
										<Select
											onValueChange={(value) => {
												setFilters({ ...filters, sort: parseInt(value) });
											}}
											value={filters.sort.toString()}
										>
											<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
												<SelectValue placeholder="Sort" />
											</SelectTrigger>
											<SelectContent>
												{sortOptions.map((option, index) => (
													<SelectItem key={index} value={index.toString()}>
														{option}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{/* Contest Filter */}
									<div className="space-y-1">
										<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
											Contest
										</label>
										<Select
											onValueChange={async (value) => {
												const contestIndex = parseInt(value);
												const contestID = getContestIDFromIndex(contestIndex);

												setFilters({ ...filters, contestid: contestID });

												// Update PrevContest if not "Glow Game" (index 0)
												if (contestID !== 0) {
													try {
														await userAuthAPI.updateMember({
															MemberID: userStore.user?.MemberID,
															PrevContest: contestID,
														});
														console.log(
															"✅ Updated PrevContest in database:",
															contestID
														);

														// Update Zustand store to keep state in sync
														const currentUser = useUserStore.getState().user;
														userStore.setUser({
															...currentUser,
															PrevContest: contestID,
														});
														console.log(
															"✅ Updated PrevContest in Zustand store:",
															contestID
														);
													} catch (error) {
														console.error(
															"Failed to update PrevContest:",
															error
														);
													}
												}
											}}
											value={getContestIndexFromID(
												filters.contestid
											).toString()}
										>
											<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
												<SelectValue placeholder="Contest" />
											</SelectTrigger>
											<SelectContent>
												{contests.map((contest, index) => (
													<SelectItem key={index} value={index.toString()}>
														{contest}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{/* Clear Filters Button */}
									<Button
										variant="ghost"
										size="sm"
										className="w-full text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
										onClick={() => {
											if (
												JSON.stringify(filters) !==
												JSON.stringify(defaultFilters)
											) {
												setFilters(defaultFilters);
											}
										}}
									>
										Clear Filters
									</Button>
								</div>
							</div>
						</div>
					</div>

					{/* Main Content */}
					<div className="min-w-0 flex-1">
						{/* Mobile Only - Full stack with spacing */}
						<div className="space-y-4 lg:hidden">
							{/* Mobile Post Type Selector - Hidden on mobile for now */}
							<div className="hidden rounded-lg border-2 border-purple-300 bg-white p-3 shadow-lg dark:border-purple-700 dark:bg-gray-800">
								<div className="flex flex-wrap justify-center gap-2">
									{postTypes.map((type) => {
										const Icon = type.icon;
										const isSelected = selectedPostType === type.id;
										// Get border color based on type
										const getBorderColor = () => {
											if (type.id === "grateful") {
												return "border-green-500";
											}
											if (type.id === "pride") {
												return "border-purple-500";
											}
											if (type.id === "glow") {
												return "border-yellow-500";
											}
											return "border-gray-300";
										};
										return (
											<button
												key={type.id}
												onClick={() => {
													// Toggle: if already selected, default to "glow"; otherwise select
													setSelectedPostType(isSelected ? "glow" : type.id);
												}}
												className={`flex min-w-[90px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-2 px-3 py-2 transition-colors ${
													isSelected
														? `${type.color} text-white ${getBorderColor()}`
														: `bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 ${getBorderColor()}`
												}`}
											>
												<Icon size={16} className="flex-shrink-0" />
												<span className="text-sm">{type.label}</span>
											</button>
										);
									})}
								</div>
							</div>

							{/* Mobile Post Type Filters */}
							<div className="rounded-lg border-2 border-purple-300 bg-white p-3 shadow-lg dark:border-purple-700 dark:bg-gray-800 lg:hidden">
								<div className="mb-3 flex items-center justify-between">
									<h3 className="text-sm font-bold text-gray-900 dark:text-white">
										Filter Posts
									</h3>
									{hasActiveFilters && (
										<button
											onClick={clearPostTypeFilters}
											className="text-xs text-purple-600 underline hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
										>
											Show All
										</button>
									)}
								</div>
								<div className="flex flex-wrap justify-center gap-2">
									{postTypes.map((type) => {
										const Icon = type.icon;
										const isFiltered = postTypeFilters[type.id];
										// Get border color based on type
										const getBorderColor = () => {
											if (type.id === "grateful") {
												return "border-green-500";
											}
											if (type.id === "pride") {
												return "border-purple-500";
											}
											if (type.id === "glow") {
												return "border-yellow-500";
											}
											return "border-gray-300";
										};
										return (
											<button
												key={type.id}
												onClick={() => togglePostTypeFilter(type.id)}
												className={`flex min-w-[90px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-2 px-3 py-2 transition-colors ${
													isFiltered
														? `${type.color} text-white ${getBorderColor()}`
														: `bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 ${getBorderColor()}`
												}`}
											>
												<Icon size={16} className="flex-shrink-0" />
												<span className="text-sm">{type.label}</span>
												{isFiltered && (
													<X
														size={14}
														className="ml-1 text-white opacity-80 hover:opacity-100"
														onClick={(e) => {
															e.stopPropagation();
															togglePostTypeFilter(type.id);
														}}
													/>
												)}
											</button>
										);
									})}
								</div>
							</div>

							{/* Mobile Filters Section */}
							<div className="overflow-hidden rounded-lg border-2 border-purple-300 bg-white shadow-lg dark:border-purple-700 dark:bg-gray-800 lg:hidden">
								{/* Filter Toggle Button */}
								<button
									onClick={() => setShowFilters(!showFilters)}
									className="flex w-full items-center justify-between p-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
								>
									<h3 className="text-sm font-bold text-gray-900 dark:text-white">
										Filters
									</h3>
									{showFilters ? (
										<ChevronUp className="h-4 w-4 text-gray-500" />
									) : (
										<ChevronDown className="h-4 w-4 text-gray-500" />
									)}
								</button>

								{/* Collapsible Filter Content */}
								<div
									className={`border-t border-gray-200 transition-all duration-300 ease-in-out dark:border-gray-700 ${
										showFilters
											? "max-h-[600px] opacity-100"
											: "max-h-0 border-t-0 opacity-0"
									}`}
									style={{ overflow: "hidden" }}
								>
									<div className="space-y-4 p-4 pt-4">
										{/* Show Comments Checkbox */}
										<label className="flex cursor-pointer items-center gap-2 text-sm">
											<input
												type="checkbox"
												checked={showComments}
												onChange={(e) => setShowComments(e.target.checked)}
												className="h-4 w-4 rounded border-gray-300 bg-gray-100 text-purple-600 accent-purple-600 focus:ring-2 focus:ring-purple-500 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800 dark:focus:ring-purple-600"
											/>
											<span className="text-gray-600 dark:text-gray-400">
												Show Comments
											</span>
										</label>

										{/* Sort Filter */}
										<div className="space-y-1">
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
												Sort By
											</label>
											<Select
												onValueChange={(value) => {
													setFilters({ ...filters, sort: parseInt(value) });
												}}
												value={filters.sort.toString()}
											>
												<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
													<SelectValue placeholder="Sort" />
												</SelectTrigger>
												<SelectContent>
													{sortOptions.map((option, index) => (
														<SelectItem key={index} value={index.toString()}>
															{option}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										{/* Contest Filter */}
										<div className="space-y-1">
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
												Contest
											</label>
											<Select
												onValueChange={(value) => {
													setFilters({
														...filters,
														contestid: parseInt(value),
													});
												}}
												value={filters.contestid.toString()}
											>
												<SelectTrigger className="w-full border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-700">
													<SelectValue placeholder="Contest" />
												</SelectTrigger>
												<SelectContent>
													{contests.map((contest, index) => (
														<SelectItem key={index} value={index.toString()}>
															{contest}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										{/* Clear Filters Button */}
										<Button
											variant="ghost"
											size="sm"
											className="w-full text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
											onClick={() => {
												if (
													JSON.stringify(filters) !==
													JSON.stringify(defaultFilters)
												) {
													setFilters(defaultFilters);
												}
											}}
										>
											Clear Filters
										</Button>
									</div>
								</div>
							</div>
						</div>

						{/* Posts section - Both mobile and desktop */}
						<div className="mt-4 min-w-0 space-y-4">
							{/* Contest Switcher */}
							{contests.length > 0 && (
								<div className="rounded-lg border-2 border-purple-300 bg-white px-4 py-3 shadow-lg dark:border-purple-700 dark:bg-gray-800">
									<div className="flex w-full min-w-0 items-center justify-between gap-2 sm:gap-4">
										{contests.length > 1 && (
											<button
												onClick={async () => {
													const currentIndex = getContestIndexFromID(
														filters.contestid
													);
													const newIndex =
														currentIndex === 0
															? contests.length - 1
															: currentIndex - 1;
													const contestID = getContestIDFromIndex(newIndex);

													setFilters({ ...filters, contestid: contestID });

													// Update PrevContest if not "Glow Game"
													if (contestID !== 0) {
														try {
															await userAuthAPI.updateMember({
																MemberID: userStore.user?.MemberID,
																PrevContest: contestID,
															});
															console.log(
																"✅ Updated PrevContest in database:",
																contestID
															);

															const currentUser = useUserStore.getState().user;
															useUserStore.setState({
																user: {
																	...currentUser,
																	PrevContest: contestID,
																},
															});
														} catch (error) {
															console.error(
																"❌ Failed to update PrevContest:",
																error
															);
														}
													}
												}}
												className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-purple-300 bg-white font-bold text-purple-600 shadow-sm transition-colors hover:bg-purple-100 dark:border-purple-600 dark:bg-gray-700 dark:text-purple-400 dark:hover:bg-purple-800"
											>
												‹
											</button>
										)}
										<div className="min-w-0 flex-1 text-center">
											<div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 sm:text-sm">
												Viewing
											</div>
											<div
												className={`truncate text-xl font-black tracking-tight sm:text-2xl ${
													contests[
														getContestIndexFromID(filters.contestid)
													].toLowerCase() === "glow game"
														? "text-orange-400 drop-shadow-[0_2px_8px_rgba(251,146,60,0.6)] dark:text-orange-200"
														: "bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent drop-shadow-[0_2px_8px_rgba(168,85,247,0.4)]"
												}`}
											>
												{contests[getContestIndexFromID(filters.contestid)]}
											</div>
										</div>
										{contests.length > 1 && (
											<button
												onClick={async () => {
													const currentIndex = getContestIndexFromID(
														filters.contestid
													);
													const newIndex = (currentIndex + 1) % contests.length;
													const contestID = getContestIDFromIndex(newIndex);

													setFilters({ ...filters, contestid: contestID });

													// Update PrevContest if not "Glow Game"
													if (contestID !== 0) {
														try {
															await userAuthAPI.updateMember({
																MemberID: userStore.user?.MemberID,
																PrevContest: contestID,
															});
															console.log(
																"✅ Updated PrevContest in database:",
																contestID
															);

															const currentUser = useUserStore.getState().user;
															useUserStore.setState({
																user: {
																	...currentUser,
																	PrevContest: contestID,
																},
															});
														} catch (error) {
															console.error(
																"❌ Failed to update PrevContest:",
																error
															);
														}
													}
												}}
												className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-purple-300 bg-white font-bold text-purple-600 shadow-sm transition-colors hover:bg-purple-100 dark:border-purple-600 dark:bg-gray-700 dark:text-purple-400 dark:hover:bg-purple-800"
											>
												›
											</button>
										)}
									</div>
								</div>
							)}

							{/* Create Post Button */}
							<button
								onClick={() => {
									setSelectedPostType(null); // Reset post type so selector shows
									setShowCreatePost(true);
								}}
								className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-3 shadow-lg transition-colors hover:from-purple-700 hover:to-pink-700 sm:p-4"
							>
								<Plus size={20} className="text-white" />
								<span className="text-sm font-medium text-white">
									Create Post
								</span>
							</button>

							{/* Post list */}
							<div className="space-y-4">
								{loading && filteredPosts.length === 0 ? (
									// Show skeleton loaders when initially loading
									Array.from({ length: 5 }).map((_, i) => (
										<Card
											key={i}
											className="border-2 border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
										>
											<CardHeader className="pb-2">
												<div className="flex items-start justify-between">
													<div className="flex flex-1 items-center gap-3">
														<Skeleton className="h-10 w-10 rounded-full bg-muted dark:bg-muted" />
														<div className="flex-1 space-y-2">
															<div className="flex items-center gap-2">
																<Skeleton className="h-5 w-24 bg-muted dark:bg-muted" />
																<Skeleton className="h-5 w-16 rounded-full bg-muted dark:bg-muted" />
															</div>
															<Skeleton className="h-4 w-20 bg-muted dark:bg-muted" />
														</div>
													</div>
												</div>
											</CardHeader>
											<CardContent className="space-y-3 pb-2">
												<Skeleton className="h-6 w-3/4 bg-muted dark:bg-muted" />
												<Skeleton className="h-4 w-full bg-muted dark:bg-muted" />
												<Skeleton className="h-4 w-5/6 bg-muted dark:bg-muted" />
												<Skeleton className="h-48 w-full rounded-md bg-muted dark:bg-muted" />
											</CardContent>
											<CardFooter className="flex items-center gap-4 pt-2">
												<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
												<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
												<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
											</CardFooter>
										</Card>
									))
								) : (
									<>
										{filteredPosts.map((item, index) => {
											return (
												<PostItem
													key={item.GlowID || item.PrideID || item.GratitudeID}
													item={item}
													lastItemRef={
														index === filteredPosts.length - 1
															? lastItemRef
															: null
													}
													isCompact={true}
													onPostSelected={onPostSelected}
													showComments={showComments}
													currentContestFilter={filters.contestid}
													selectedCommentsPost={selectedCommentsPost}
													setSelectedCommentsPost={setSelectedCommentsPost}
													onSendLumens={() => {
														const itemId =
															item.GlowID || item.PrideID || item.GratitudeID;
														setSelectedPostForLumens(itemId);
														setShowSendLumens(true);
													}}
												/>
											);
										})}

										{/* Loading more indicator */}
										{loading && filteredPosts.length > 0 && (
											<div className="space-y-3">
												{Array.from({ length: 2 }).map((_, i) => (
													<Card
														key={i}
														className="border-2 border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
													>
														<CardHeader className="pb-2">
															<div className="flex items-start justify-between">
																<div className="flex flex-1 items-center gap-3">
																	<Skeleton className="h-10 w-10 rounded-full bg-muted dark:bg-muted" />
																	<div className="flex-1 space-y-2">
																		<div className="flex items-center gap-2">
																			<Skeleton className="h-5 w-24 bg-muted dark:bg-muted" />
																			<Skeleton className="h-5 w-16 rounded-full bg-muted dark:bg-muted" />
																		</div>
																		<Skeleton className="h-4 w-20 bg-muted dark:bg-muted" />
																	</div>
																</div>
															</div>
														</CardHeader>
														<CardContent className="space-y-3 pb-2">
															<Skeleton className="h-6 w-3/4 bg-muted dark:bg-muted" />
															<Skeleton className="h-4 w-full bg-muted dark:bg-muted" />
															<Skeleton className="h-4 w-5/6 bg-muted dark:bg-muted" />
															<Skeleton className="h-48 w-full rounded-md bg-muted dark:bg-muted" />
														</CardContent>
														<CardFooter className="flex items-center gap-4 pt-2">
															<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
															<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
															<Skeleton className="h-8 w-20 bg-muted dark:bg-muted" />
														</CardFooter>
													</Card>
												))}
											</div>
										)}
									</>
								)}

								{/* Last item reference for intersection observer */}
								{newsfeed.hasMorePosts && !loading && (
									<div ref={lastItemRef} className="h-1" />
								)}

								{/* Back to Top - Always show */}
								<button
									type="button"
									onClick={() => {
										// Find and scroll the container to the very top
										const scrollContainer =
											document.querySelector(".overflow-auto");
										if (scrollContainer) {
											// Use scrollTo with top: 0 for smooth scroll to absolute top
											scrollContainer.scrollTo({
												top: 0,
												left: 0,
												behavior: "smooth",
											});
										} else {
											window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
										}
									}}
									className="mb-[300px] flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-center text-white shadow-lg transition-colors hover:from-purple-700 hover:to-pink-700 md:mb-64"
								>
									<ArrowUp size={20} />
									<span>Back to Top</span>
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Send Lumens Modal */}
				{showSendLumens && (
					<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 dark:bg-black/50">
						<div className="my-auto w-full max-w-md space-y-4 rounded-lg bg-white p-4 shadow-xl dark:bg-gray-800 sm:p-6">
							<div className="flex items-center justify-between">
								<h2 className="text-xl font-bold text-gray-900 dark:text-white">
									Send Lumens
								</h2>
								<button
									onClick={() => {
										setShowSendLumens(false);
										setSelectedPostForLumens(null);
									}}
									className="rounded bg-gray-200 p-1 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
								>
									<X size={20} />
								</button>
							</div>

							{/* Current Balance Display */}
							<div className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-yellow-400 to-amber-500 p-3 dark:from-yellow-500 dark:to-amber-600">
								<Star className="h-5 w-5 text-white" />
								<span className="text-sm font-bold text-white">
									Your Balance:{" "}
									{(userStore.user?.CurrentLumens || 0).toLocaleString()} Lumens
								</span>
							</div>

							<div className="space-y-4">
								<div className="space-y-2">
									<label className="text-sm font-medium text-gray-900 dark:text-white">
										Amount to Send
									</label>
									<input
										type="number"
										min="1"
										value={lumensToSend === 0 ? "" : lumensToSend}
										onChange={(e) => {
											const value = e.target.value;
											if (value === "") {
												setLumensToSend(0);
											} else {
												setLumensToSend(parseInt(value) || 0);
											}
										}}
										placeholder="0"
										className="w-full rounded-lg border border-gray-200 bg-gray-100 p-3 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white"
									/>
								</div>

								<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
									{[5, 10, 25, 50].map((amount) => (
										<button
											key={amount}
											onClick={() => setLumensToSend(amount)}
											className={`rounded-lg p-2 text-sm transition-colors ${
												lumensToSend === amount
													? "bg-yellow-500 text-black"
													: "bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
											}`}
										>
											{amount}L
										</button>
									))}
								</div>
							</div>

							<div className="flex gap-3">
								<button
									onClick={() => {
										setShowSendLumens(false);
										setSelectedPostForLumens(null);
									}}
									className="flex-1 rounded-lg bg-gray-200 p-3 text-gray-900 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
								>
									Cancel
								</button>
								<button
									onClick={async () => {
										// Get the post to find the recipient MemberID
										const post = newsfeed.posts.find(
											(p) => p.GlowID === selectedPostForLumens
										);
										if (!post) {
											toast.error("Post not found");
											return;
										}

										// Prevent sending lumens to yourself
										if (post.MemberID === userStore.user?.MemberID) {
											toast.error("You cannot send lumens to yourself!");
											return;
										}

										// Use the Zustand store handler for optimistic UI updates
										const result = await newsfeed.handleSendLumens(
											selectedPostForLumens,
											lumensToSend,
											post.MemberID
										);

										if (result.success) {
											setShowSendLumens(false);
											setSelectedPostForLumens(null);
											setLumensToSend(5); // Reset to default
										}
									}}
									disabled={lumensToSend < 1}
									className="flex-1 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-3 text-white transition-colors hover:from-purple-700 hover:to-pink-700 disabled:cursor-not-allowed disabled:opacity-50"
								>
									Send {lumensToSend}L
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Create Post Modal */}
				{showCreatePost && (
					<div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-2 dark:bg-black/50 sm:p-4">
						<div className="my-auto w-full max-w-xl">
							<ComposePost
								onContestChange={(contestID) => {
									console.log(
										"📝 ComposePost contest changed to ID:",
										contestID
									);
									setFilters({ ...filters, contestid: contestID });
								}}
								currentContestId={filters.contestid}
								selectedPostType={selectedPostType}
								onPostTypeSelect={setSelectedPostType}
								borderColor={
									selectedPostType === "grateful"
										? "border-green-500"
										: selectedPostType === "pride"
											? "border-purple-500"
											: "border-yellow-500"
								}
								onPostCreated={() => setShowCreatePost(false)}
								onClose={() => setShowCreatePost(false)}
							/>
						</div>
					</div>
				)}

				{/* Invite Confirmation Modal */}
				<InviteConfirmationModal
					isOpen={shouldShowInviteModal}
					onClose={() => setShowModal(false)}
					inviteData={inviteData}
					isLoading={isProcessing}
				/>
			</div>
		);

		// Single post display page
	} else if (selectedPost) {
		console.log("✅ Rendering single post:", selectedPost);
		return (
			<div className="relative mx-auto h-full w-full max-w-xl bg-background p-4 dark:bg-background">
				<Button
					className="mb-2 border-border bg-background text-foreground hover:bg-muted/50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted/50"
					variant="outline"
					onClick={() => {
						navigate("/newsfeed");
						newsfeed.getPosts(1, filters);
					}}
				>
					Back
				</Button>
				<PostItem
					item={selectedPost}
					isCompact={false}
					onPostSelected={onPostSelected}
					currentContestFilter={filters.contestid}
				/>
			</div>
		);
	} else {
		console.log("showPost else block:", {
			showPost,
			selectedPost,
			loading,
			id,
		});
		return (
			<div className="mx-auto w-full max-w-xl p-4 text-foreground dark:text-foreground">
				{loading ? "Loading post..." : "Post not found"}
			</div>
		);
	}
};

export default Newsfeed;
